import React, { useState, useEffect } from 'react';
import { 
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  HeartIcon,
  PhotoIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import axios from 'axios';
import api from '../../services/api';
import API_CONFIG from '../../config/api';

// Componente SearchableSelect
const SearchableSelect = ({ options, value, onChange, placeholder, name }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(
        options.filter(option => 
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [searchTerm, options]);
  
  return (
    <div className="relative">
      <div
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm cursor-pointer bg-white focus-within:ring-blue-500 focus-within:border-blue-500"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex justify-between items-center">
          <span className={`block truncate ${!value ? 'text-gray-400' : 'text-gray-900'}`}>
            {value ? options.find(opt => opt.value === value)?.label || value : placeholder}
          </span>
          <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto">
          <div className="sticky top-0 bg-white px-3 py-2 border-b">
            <input
              type="text"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              placeholder="Cerca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div
            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50"
            onClick={() => {
              onChange({ target: { value: '' } });
              setIsOpen(false);
              setSearchTerm('');
            }}
          >
            <span className="block truncate text-gray-400">{placeholder}</span>
          </div>
          {filteredOptions.map((option) => (
            <div
              key={option.value}
              className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50"
              onClick={() => {
                onChange({ target: { value: option.value } });
                setIsOpen(false);
                setSearchTerm('');
              }}
            >
              <span className={`block truncate ${option.value === value ? 'font-medium text-blue-600' : 'font-normal text-gray-900'}`}>
                {option.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TabOfferte = ({ 
  pratica, 
  isOfferteTabDisabled,
  ruoloUtente,
  onOffertaSelezionata,
  currentUser
}) => {
  // Stati per gestione offerte
  const [offertePublic, setOffertePublic] = useState([]);
  const [loadingOffertePublic, setLoadingOffertePublic] = useState(false);
  const [vetturePublic, setVetturePublic] = useState([]);
  const [marchePublic, setMarchePublic] = useState([]);
  const [scadenzeOfferte, setScadenzeOfferte] = useState({});
  
  // Stati per filtri
  const [searchQuery, setSearchQuery] = useState('');
  const [filtroTipoCliente, setFiltroTipoCliente] = useState('');
  const [filtroMarca, setFiltroMarca] = useState('');
  const [filtroAlimentazione, setFiltroAlimentazione] = useState('');
  const [filtroBudgetMin, setFiltroBudgetMin] = useState('');
  const [filtroBudgetMax, setFiltroBudgetMax] = useState('');

  // Stati per gestione modale
  const [showModalPublic, setShowModalPublic] = useState(false);
  const [selectedVehiclePublic, setSelectedVehiclePublic] = useState(null);
  const [vehicleOffertePublic, setVehicleOffertePublic] = useState([]);
  const [currentFilterIndex, setCurrentFilterIndex] = useState(0);
  const [durataFilter, setDurataFilter] = useState('');
  const [kmAnnuiFilter, setKmAnnuiFilter] = useState('');
  const [tipoClienteFilter, setTipoClienteFilter] = useState('');
  const [quantitaOfferta, setQuantitaOfferta] = useState(1);
  // Nuovo stato per le scadenze
  const [loadingScadenze, setLoadingScadenze] = useState(false);
  // Stato per il filtro anticipo per valore
  const [anticipoFilterPublic, setAnticipoFilterPublic] = useState('');
  
  // Stati per calcolo provvigioni
  const [showProvvigioni, setShowProvvigioni] = useState(false);
  const [provvigioniData, setProvvigioniData] = useState(null);
  const [loadingProvvigioni, setLoadingProvvigioni] = useState(false);

  // Stati che bloccano la richiesta di preventivi per le agenzie
  const statiBloccanti = [
    'valutazione_cliente',
    'attesa_documenti_cliente', 
    'richiesta_esito',
    'pratica_in_valutazione',
    'attesa_firma_contratto',
    'contratto_attivo',
    'richiesta_integrazione',
    'pratica_chiusa'
  ];

  // Funzione per verificare se l'utente è un'agenzia
  const isAgenzia = () => {
    return ruoloUtente === 'agenzia' || ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale';
  };

  // Funzione per verificare se la richiesta di preventivi è bloccata
  const isPreventiviBloccati = () => {
    return isAgenzia() && statiBloccanti.includes(pratica?.statoPratica);
  };

  // Carica le offerte quando si attiva il tab
  useEffect(() => {
    fetchOffertePublic();
  }, []);

  // Aggiorna il filtro tipo cliente automaticamente in base al cliente della pratica
  useEffect(() => {
    if (pratica?.cliente?.tipo_cliente) {
      // Converte il valore dal database al formato del select HTML
      const tipoClienteCapitalized = pratica.cliente.tipo_cliente.toLowerCase() === 'privato' ? 'Privato' : 
                                    pratica.cliente.tipo_cliente.toLowerCase() === 'azienda' ? 'Azienda' : 
                                    pratica.cliente.tipo_cliente;
      setFiltroTipoCliente(tipoClienteCapitalized);
    }
  }, [pratica]);

  const fetchOffertePublic = async () => {
    setLoadingOffertePublic(true);
    try {
      // Richiediamo tutti gli elementi, non solo la prima pagina
      const response = await axios.get(`${API_CONFIG.baseURL}/api/offerte-vetture?page=1&limit=5000`);
      
      console.log('🔍 Response completa API offerte-vetture:', response);
      console.log('🔍 Response.data:', response.data);
      
      // L'API ritorna: {totalItems, totalPages, currentPage, items}
      if (response.data && response.data.items) {
        const offerte = response.data.items || [];
        console.log('✅ Offerte caricate:', offerte.length);
        console.log('🔍 Prima offerta:', offerte[0]);
        setOffertePublic(offerte);
        
        // Estrai vetture uniche
        const vettureMap = new Map();
        const marcheSet = new Set();
        
        offerte.forEach(offerta => {
          if (offerta.vettura || offerta.Vettura) {
            // Gestisce sia 'vettura' che 'Vettura' (maiuscolo)
            const vetturaObj = offerta.vettura || offerta.Vettura;
            vettureMap.set(vetturaObj.id, vetturaObj);
            if (vetturaObj.marca?.descrizione) {
              marcheSet.add(vetturaObj.marca.descrizione);
            } else if (vetturaObj.brand) {
              marcheSet.add(vetturaObj.brand);
            }
          }
        });
        
        console.log('🚗 Vetture uniche estratte:', vettureMap.size);
        console.log('🏷️ Marche estratte:', Array.from(marcheSet));
        
        setVetturePublic(Array.from(vettureMap.values()));
        setMarchePublic(Array.from(marcheSet));
        
        // Carica le scadenze
        if (vettureMap.size > 0) {
          await fetchScadenzePublic(Array.from(vettureMap.keys()));
        }
      } else {
        console.log('❌ Response.data non ha items:', response.data);
        setOffertePublic([]);
        setVetturePublic([]);
        setMarchePublic([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento offerte pubbliche:', error);
      if (error.response?.status === 404) {
        console.info('Endpoint offerte pubbliche non trovato, usando lista vuota');
      } else {
        toast.error('Errore nel caricamento delle offerte');
      }
      setOffertePublic([]);
      setVetturePublic([]);
      setMarchePublic([]);
    } finally {
      setLoadingOffertePublic(false);
    }
  };

  const fetchScadenzePublic = async (vettureIds) => {
    try {
      const response = await axios.post(`${API_CONFIG.baseURL}/api/offerte-public/scadenze`, {
        vettureIds: vettureIds
      });
      
      if (response.data && response.data.success) {
        setScadenzeOfferte(response.data.data || {});
      }
    } catch (error) {
      console.error('Errore nel caricamento scadenze:', error);
      setScadenzeOfferte({});
    }
  };

  const formatPrezzo = (prezzo) => {
    if (!prezzo || isNaN(prezzo)) return '€0';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(parseFloat(prezzo));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('it-IT');
    } catch (error) {
      return '';
    }
  };

  const getVehicleImage = (vettura) => {
    if (!vettura) return null;
    
    // Gestisce immagine come oggetto con path_file
    if (vettura.immagine && typeof vettura.immagine === 'object' && vettura.immagine.path_file) {
      return `${window.location.origin}${vettura.immagine.path_file}`;
    }
    
    // Gestisce immagine come stringa
    if (vettura.immagine && typeof vettura.immagine === 'string') {
      if (vettura.immagine.startsWith('http')) {
        return vettura.immagine;
      }
      if (vettura.immagine.startsWith('/')) {
        return `${window.location.origin}${vettura.immagine}`;
      }
      return `${API_CONFIG.baseURL}${vettura.immagine}`;
    }
    
    // Gestisce campo img
    if (vettura.img && typeof vettura.img === 'string') {
      if (vettura.img.startsWith('http')) {
        return vettura.img;
      }
      if (vettura.img.startsWith('/')) {
        return `${window.location.origin}${vettura.img}`;
      } else if (vettura.img.startsWith('../')) {
        return vettura.img.replace('../', '/');
      }
      return vettura.img;
    }
    
    // Gestisce campo image
    if (vettura.image && typeof vettura.image === 'string') {
      if (vettura.image.startsWith('http')) {
        return vettura.image;
      }
      if (vettura.image.startsWith('/')) {
        return `${window.location.origin}${vettura.image}`;
      }
      return `${API_CONFIG.baseURL}${vettura.image}`;
    }
    
    return null;
  };

  const getMarcheOptions = () => {
    return marchePublic.map(marca => ({
      value: marca,
      label: marca
    }));
  };

  const getAlimentazioniOptions = () => {
    const alimentazioni = [...new Set(vetturePublic.map(v => v.alimentazione).filter(Boolean))];
    return alimentazioni.map(alimentazione => ({
      value: alimentazione,
      label: alimentazione
    }));
  };

  const openPreventivatorePublic = (vettura) => {
    setSelectedVehiclePublic(vettura);
    
    const offerteVettura = offertePublic.filter(offerta => {
      const vetturaObj = offerta.vettura || offerta.Vettura;
      return vetturaObj && vetturaObj.id === vettura.id;
    });
    
    const durateUniche = [...new Set(offerteVettura.map(o => o.durata))].sort((a, b) => a - b);
    const kmAnnuiUnici = [...new Set(offerteVettura.map(o => o.kmAnnui))].sort((a, b) => a - b);
    
    setDurataFilter(durateUniche.length > 0 ? durateUniche[0].toString() : '');
    setKmAnnuiFilter(kmAnnuiUnici.length > 0 ? kmAnnuiUnici[0].toString() : '');
    
    // Usa automaticamente il tipo cliente della pratica
    const tipoClientePratica = pratica?.cliente?.tipo_cliente;
    let offerteFiltratePerTipoCliente = offerteVettura;
    
    if (tipoClientePratica) {
      // Converte il valore del filtro (con maiuscola) al formato del database (minuscolo)
      const filtroNormalizzato = tipoClientePratica.toLowerCase().trim();
      console.log('🎯 Filtro tipo cliente:', {
        tipoClientePratica,
        filtroNormalizzato,
        vettura: vettura.modello,
        offerteVettura: offerteVettura.length
      });
      
      offerteFiltratePerTipoCliente = offerteVettura.filter(offerta => {
        const tipoClienteOfferta = (offerta.tipoCliente || '').toLowerCase().trim();
        const tipoClienteOriginale = (offerta.tipoCliente || '').trim();
        
        // Confronta con valori normalizzati e gestisce valori maiuscoli dal database
        const isCompatible = tipoClienteOfferta === filtroNormalizzato || 
                            tipoClienteOfferta === 'tutti' || 
                            tipoClienteOfferta === '' ||
                            tipoClienteOriginale === 'TUTTI' ||
                            tipoClienteOriginale === 'PRIVATO' && filtroNormalizzato === 'privato' ||
                            tipoClienteOriginale === 'AZIENDA' && filtroNormalizzato === 'azienda';
        
        console.log('🔍 Controllo offerta:', {
          offertaId: offerta.id,
          tipoClienteOfferta,
          tipoClienteOriginale: offerta.tipoCliente,
          filtroNormalizzato,
          isCompatible
        });
        
        return isCompatible;
      });

      // Se non ci sono offerte compatibili per questo tipo cliente, non mostrare la vettura
      if (offerteFiltratePerTipoCliente.length === 0) {
        console.log('❌ Nessuna offerta compatibile per tipo cliente', filtroNormalizzato, 'vettura:', vettura.modello);
        return null;
      }
    }
    
    setTipoClienteFilter(tipoClientePratica || '');
    setVehicleOffertePublic(offerteFiltratePerTipoCliente);
    setCurrentFilterIndex(0);
    setShowModalPublic(true);
  };

  const associaOffertaPratica = async (offertaSelezionata) => {
    try {
      const response = await fetch(`/api/pratica/${pratica.id}/preventivi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          offertaData: { ...offertaSelezionata, quantita: parseInt(quantitaOfferta) || 1 },
          tipoPreventivo: 'alternativo',
          note: `Preventivo creato tramite catalogo: ${offertaSelezionata.vettura?.marca?.descrizione || offertaSelezionata.vettura?.brand} ${offertaSelezionata.vettura?.modello} - €${offertaSelezionata.canone}/mese${(parseInt(quantitaOfferta) || 1) > 1 ? ` ×${parseInt(quantitaOfferta)}` : ''}`,
          creatoDA: currentUser?.nome || 'Sistema'
        })
      });

      if (response.ok) {
        const result = await response.json();
        setShowModalPublic(false);
        setSelectedVehiclePublic(null);
        setVehicleOffertePublic([]);
        setDurataFilter('');
        setKmAnnuiFilter('');
        setTipoClienteFilter('');
        setAnticipoFilterPublic('');
        setQuantitaOfferta(1);
        setCurrentFilterIndex(0);
        setShowProvvigioni(false);
        setProvvigioniData(null);
        toast.success('Preventivo creato con successo!');
        // Callback per aggiornare i dati della pratica
        if (onOffertaSelezionata) {
          onOffertaSelezionata();
        }
      } else {
        const error = await response.json();
        toast.error(`Errore nella creazione del preventivo: ${error.error}`);
      }
    } catch (error) {
      console.error('Errore nella creazione del preventivo:', error);
      toast.error('Errore di connessione nella creazione del preventivo');
    }
  };

  // Funzioni helper per il modale
  const getFilteredOffertePublic = () => {
    return vehicleOffertePublic.filter(offerta => 
      (durataFilter ? offerta.durata.toString() === durataFilter : true) &&
      (kmAnnuiFilter ? offerta.kmAnnui.toString() === kmAnnuiFilter : true) &&
      (anticipoFilterPublic !== '' && anticipoFilterPublic !== null && anticipoFilterPublic !== undefined ? 
        (offerta.anticipo || 0).toString() === anticipoFilterPublic : true)
    );
  };

  const getFilteredAndSortedOffertePublic = () => {
    const filtered = getFilteredOffertePublic();
    return filtered.sort((a, b) => parseFloat(a.canone) - parseFloat(b.canone));
  };

  const getCurrentOffertaPublic = () => {
    const filteredOfferte = getFilteredAndSortedOffertePublic();
    if (filteredOfferte.length === 0) return null;
    
    if (currentFilterIndex >= filteredOfferte.length) {
      setCurrentFilterIndex(0);
    }
    
    return filteredOfferte[currentFilterIndex];
  };

  const formatPenaleValue = (value) => {
    if (!value || value === 0) return 'Nessuna';
    
    if (value < 0) {
      return `${Math.abs(value)}%`;
    }
    
    return `${value}€`;
  };

  const navigateOffertePublic = (direction) => {
    const filteredOfferte = getFilteredAndSortedOffertePublic();
    console.log('🔍 Navigate offerte public called:', {
      direction,
      filteredOfferteLength: filteredOfferte.length,
      currentFilterIndex,
      durataFilter,
      kmAnnuiFilter,
      anticipoFilterPublic
    });
    
    if (filteredOfferte.length === 0) {
      console.log('❌ Nessuna offerta filtrata disponibile');
      return;
    }
    
    if (direction === 'next') {
      const newIndex = (currentFilterIndex + 1) % filteredOfferte.length;
      console.log('➡️ Next: da', currentFilterIndex, 'a', newIndex);
      setCurrentFilterIndex(newIndex);
    } else {
      const newIndex = (currentFilterIndex - 1 + filteredOfferte.length) % filteredOfferte.length;
      console.log('⬅️ Prev: da', currentFilterIndex, 'a', newIndex);
      setCurrentFilterIndex(newIndex);
    }
  };

  // Funzione per calcolare le provvigioni
  const calcolaProvvigioni = async () => {
    const offertaCorrente = getCurrentOffertaPublic();
    if (!offertaCorrente) {
      setProvvigioniData({ errore: 'Nessuna offerta selezionata' });
      return;
    }

    setLoadingProvvigioni(true);
    try {
      // Simula un contratto temporaneo per il calcolo delle provvigioni
      const contrattoTemp = {
        id: `temp_${Date.now()}`,
        canone: offertaCorrente.canone,
        durata: offertaCorrente.durata,
        provvigione: offertaCorrente.provvigione || 0,
        offertaId: offertaCorrente.id,
        tipoNodo: 'agenzia_primaria', // Tipo di default per l'utente corrente
        importoTotale: offertaCorrente.canone * offertaCorrente.durata
      };

      console.log('📊 Calcolo provvigioni per offerta:', {
        canone: contrattoTemp.canone,
        durata: contrattoTemp.durata,
        provvigione: contrattoTemp.provvigione,
        importoTotale: contrattoTemp.importoTotale
      });

      const response = await api.post(
        '/provvigioni-contratti/calcola-distribuzione',
        { contratto: contrattoTemp },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        // Normalizza i dati per gestire sia admin che non admin
        const dataProvvigioni = response.data.data;
        const provvigioneDisplay = dataProvvigioni.provvigioneTotale || dataProvvigioni.provvigionePersonale || 0;
        
        setProvvigioniData({
          ...dataProvvigioni,
          provvigioneTotale: provvigioneDisplay // Normalizza per il display
        });
        console.log('✅ Provvigioni calcolate:', dataProvvigioni);
      } else {
        throw new Error(response.data.message || 'Errore nel calcolo delle provvigioni');
      }
    } catch (error) {
      console.error('❌ Errore calcolo provvigioni:', error);
      toast.error('Errore nel calcolo delle provvigioni: ' + (error.response?.data?.message || error.message));
      setProvvigioniData({
        errore: 'Impossibile calcolare le provvigioni al momento',
        distribuzione: [],
        provvigioneTotale: 0
      });
    } finally {
      setLoadingProvvigioni(false);
    }
  };

  // Funzione per formattare la valuta
  const formatCurrency = (amount) => {
    if (!amount) return '€0,00';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Funzione per mostrare/nascondere le provvigioni
  const toggleProvvigioni = async () => {
    if (!showProvvigioni) {
      setShowProvvigioni(true);
      await calcolaProvvigioni();
    } else {
      setShowProvvigioni(false);
      setProvvigioniData(null);
    }
  };

  if (!pratica) {
    return <div>Caricamento...</div>;
  }

  // Logica di filtraggio identica all'originale
  const vetturePublicConOffertaMigliore = vetturePublic
    .filter(vettura => {
      const matchesSearch = !searchQuery || 
        (vettura.modello || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (vettura.allestimento || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (vettura.marca?.descrizione || vettura.brand || '').toLowerCase().includes(searchQuery.toLowerCase());

      const matchesMarca = !filtroMarca || 
        (vettura.marca?.descrizione === filtroMarca) || 
        (vettura.brand === filtroMarca);

      const matchesAlimentazione = !filtroAlimentazione || 
        vettura.alimentazione === filtroAlimentazione;

      return matchesSearch && matchesMarca && matchesAlimentazione;
    })
    .map(vettura => {
      const offerteVettura = offertePublic.filter(offerta => {
        const vetturaObj = offerta.vettura || offerta.Vettura;
        return vetturaObj && vetturaObj.id === vettura.id;
      });

      if (offerteVettura.length === 0) return null;

      // Filtra per tipo cliente se specificato
      let offerteFiltratePerTipoCliente = offerteVettura;
      if (filtroTipoCliente) {
        // Converte il valore del filtro (con maiuscola) al formato del database (minuscolo)
        const filtroNormalizzato = filtroTipoCliente.toLowerCase().trim();
        console.log('🎯 Filtro tipo cliente:', {
          filtroTipoCliente,
          filtroNormalizzato,
          vettura: vettura.modello,
          offerteVettura: offerteVettura.length
        });
        
        offerteFiltratePerTipoCliente = offerteVettura.filter(offerta => {
          const tipoClienteOfferta = (offerta.tipoCliente || '').toLowerCase().trim();
          const tipoClienteOriginale = (offerta.tipoCliente || '').trim();
          
          // Confronta con valori normalizzati e gestisce valori maiuscoli dal database
          const isCompatible = tipoClienteOfferta === filtroNormalizzato || 
                              tipoClienteOfferta === 'tutti' || 
                              tipoClienteOfferta === '' ||
                              tipoClienteOriginale === 'TUTTI' ||
                              tipoClienteOriginale === 'PRIVATO' && filtroNormalizzato === 'privato' ||
                              tipoClienteOriginale === 'AZIENDA' && filtroNormalizzato === 'azienda';
          
          console.log('🔍 Controllo offerta:', {
            offertaId: offerta.id,
            tipoClienteOfferta,
            tipoClienteOriginale: offerta.tipoCliente,
            filtroNormalizzato,
            isCompatible
          });
          
          return isCompatible;
        });

        // Se non ci sono offerte compatibili per questo tipo cliente, non mostrare la vettura
        if (offerteFiltratePerTipoCliente.length === 0) {
          console.log('❌ Nessuna offerta compatibile per tipo cliente', filtroNormalizzato, 'vettura:', vettura.modello);
          return null;
        }
      }

      // Trova l'offerta migliore (canone più basso)
      const offertaMigliore = offerteFiltratePerTipoCliente.reduce((min, offerta) => {
        const canoneCorrente = parseFloat(offerta.canone || 0);
        const canoneMin = parseFloat(min.canone || 0);
        return canoneCorrente < canoneMin ? offerta : min;
      });

      // Filtra per budget se specificato
      if (filtroBudgetMin && parseFloat(offertaMigliore.canone || 0) < parseFloat(filtroBudgetMin)) return null;
      if (filtroBudgetMax && parseFloat(offertaMigliore.canone || 0) > parseFloat(filtroBudgetMax)) return null;

      return { vettura, offertaMigliore };
    })
    .filter(Boolean);

  console.log('📊 Stati debug:', {
    loadingOffertePublic,
    offertePublic: offertePublic.length,
    vetturePublic: vetturePublic.length,
    vetturePublicConOffertaMigliore: vetturePublicConOffertaMigliore.length,
    filtroTipoCliente,
    searchQuery,
    filtroMarca,
    filtroAlimentazione,
    filtroBudgetMin,
    filtroBudgetMax
  });

  return (
    <>
      <div className="space-y-6">
        {/* Messaggio di avviso se la tab è disabilitata */}
        {isOfferteTabDisabled() && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Offerte temporaneamente non disponibili
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    La selezione delle offerte è disabilitata mentre la pratica è in attesa dell'elaborazione dei preventivi. 
                    Una volta che l'amministratore avrà elaborato i preventivi richiesti, sarà possibile tornare a selezionare le offerte.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Disclaimer per agenzie quando i preventivi sono bloccati */}
        {isPreventiviBloccati() && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <InformationCircleIcon className="h-5 w-5 text-blue-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Richiesta preventivi non disponibile
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Non puoi creare preventivi mentre la pratica è nello stato "{pratica?.statoPratica?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l.toUpperCase())}". 
                    Per creare altri preventivi, torna allo stato "Pratica Avviata".
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Filtri */}
        <div className={`bg-white shadow rounded-lg p-4 ${isOfferteTabDisabled() ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="mb-4 flex flex-col md:flex-row gap-4 justify-between items-center">
            {/* Barra di ricerca */}
            <div className="w-full md:w-2/3 relative">
              <input
                type="text"
                placeholder="Cerca modello o allestimento..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
            
            {/* Filtro Tipo Cliente */}
            <div className="w-full md:w-1/3 flex space-x-2">
              <span className="text-sm font-medium text-gray-700 self-center whitespace-nowrap">Tipo Cliente:</span>
              <div className="flex-1">
                <select
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-gray-100 cursor-not-allowed"
                  value={filtroTipoCliente}
                  disabled={true}
                  title={`Bloccato su: ${pratica?.cliente?.tipo_cliente || 'Non specificato'} (dal cliente della pratica)`}
                >
                  <option value="">Tutti</option>
                  <option value="Privato">Privato</option>
                  <option value="Azienda">Azienda</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Bloccato su: <span className="font-medium">{pratica?.cliente?.tipo_cliente || 'Non specificato'}</span>
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Filtro marca */}
            <div className="w-full">
              <SearchableSelect
                options={getMarcheOptions()}
                value={filtroMarca}
                onChange={(e) => setFiltroMarca(e.target.value)}
                placeholder="Tutte le marche"
                name="filtroMarca"
              />
            </div>
            
            {/* Filtro alimentazione */}
            <div className="w-full">
              <SearchableSelect
                options={getAlimentazioniOptions()}
                value={filtroAlimentazione}
                onChange={(e) => setFiltroAlimentazione(e.target.value)}
                placeholder="Alimentazione"
                name="filtroAlimentazione"
              />
            </div>
            
            {/* Filtro budget */}
            <div className="flex space-x-2 items-center">
              <div className="relative flex-1">
                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">€</span>
                <input
                  type="number"
                  placeholder="Da"
                  className="w-full pl-6 pr-2 py-2 border border-gray-300 rounded-md text-sm"
                  value={filtroBudgetMin}
                  onChange={(e) => setFiltroBudgetMin(e.target.value)}
                />
              </div>
              <span className="text-gray-500">-</span>
              <div className="relative flex-1">
                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-gray-500">€</span>
                <input
                  type="number"
                  placeholder="A"
                  className="w-full pl-6 pr-2 py-2 border border-gray-300 rounded-md text-sm"
                  value={filtroBudgetMax}
                  onChange={(e) => setFiltroBudgetMax(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
        
        {/* Numero risultati */}
        <div className="mb-4">
          <span className="font-semibold">{vetturePublicConOffertaMigliore.length} Risultati</span>
        </div>
        
        {/* Griglia offerte */}
        <div className={`pb-12 ${isOfferteTabDisabled() ? 'opacity-50 pointer-events-none' : ''}`}>
          {loadingOffertePublic ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-600">Caricamento offerte...</p>
            </div>
          ) : vetturePublicConOffertaMigliore.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <p className="text-gray-600">Nessuna offerta trovata con i filtri selezionati.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {vetturePublicConOffertaMigliore.map(({ vettura, offertaMigliore }) => (
                <div key={vettura.id} className="bg-white rounded-lg shadow overflow-hidden">
                  {/* Badge offerta */}
                  <div className="relative">
                    {/* Preferiti */}
                    <button className="absolute top-2 right-2 z-10 text-gray-100 hover:text-red-500">
                      <HeartIcon className="h-6 w-6" />
                    </button>
                    
                    {/* Immagine auto */}
                    <div className="bg-blue-50 p-4 h-48 relative">
                      {getVehicleImage(vettura) ? (
                        <img
                          src={getVehicleImage(vettura)}
                          alt={`${vettura.marca?.descrizione || vettura.brand || ''} ${vettura.modello || ''}`}
                          className="w-full h-full object-contain absolute inset-0"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="h-20 w-20 bg-blue-200 rounded-full flex items-center justify-center">
                            <PhotoIcon className="h-10 w-10 text-blue-500" />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Badge stato disponibilità */}
                    <div className="absolute bottom-0 right-0 bg-blue-600 text-white px-4 py-1">
                      {offertaMigliore.statoDisponibilita || 'Nuovo'}
                    </div>
                  </div>
                  
                  {/* Informazioni vettura */}
                  <div className="px-6 py-4 border-b border-gray-200">
                    <div className="uppercase font-bold text-gray-900">
                      {vettura.marca?.descrizione || vettura.brand || ''} {vettura.modello || ''}
                    </div>
                    <div className="text-sm text-gray-600">
                      {vettura.allestimento || ''}
                    </div>
                  </div>
                  
                  {/* Prezzo */}
                  <div className="px-6 py-4 bg-blue-50">
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold text-blue-600">
                        {formatPrezzo(offertaMigliore.canone)}<span className="text-sm font-normal text-blue-500">/mese IVA escl.</span>
                      </div>
                    </div>
                    <div className="text-xs text-blue-700 uppercase font-semibold mt-1">
                      Offerta limitata
                    </div>
                  </div>
                  
                  {/* Dettagli */}
                  <div className="px-6 py-3 text-sm text-gray-600">
                    <div className="flex justify-between">
                      <span>{offertaMigliore.durata} Mesi</span>
                      <span>{parseInt(offertaMigliore.kmAnnui || 0).toLocaleString()} Km/Anno</span>
                      <span>Anticipo</span>
                    </div>
                    <div className="mt-1">
                      {formatPrezzo(offertaMigliore.anticipo || 0)} IVA Escl.
                    </div>
                  </div>
                  
                  {/* Pulsante */}
                  <div className="px-6 py-4">
                    <button 
                      onClick={() => openPreventivatorePublic(vettura)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                    >
                      Scopri l'offerta
                    </button>
                    <div className="text-center text-xs text-gray-500 mt-2">
                      {scadenzeOfferte[vettura.id] ? `Fino al ${formatDate(scadenzeOfferte[vettura.id].scadenzaOfferte)}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal Preventivatore Pubblico */}
      {showModalPublic && selectedVehiclePublic && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            
            {/* Modal */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              {/* Header */}
              <div className="bg-white px-4 pt-4 pb-4 sm:p-6 sm:pb-4 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Configura il noleggio - {selectedVehiclePublic.marca?.descrizione || selectedVehiclePublic.brand} {selectedVehiclePublic.modello} {selectedVehiclePublic.allestimento}
                  </h3>
                  <button
                    onClick={() => {
                      setShowModalPublic(false);
                      setSelectedVehiclePublic(null);
                      setVehicleOffertePublic([]);
                      setDurataFilter('');
                      setKmAnnuiFilter('');
                      setTipoClienteFilter('');
                      setAnticipoFilterPublic('');
                      setCurrentFilterIndex(0);
                      setShowProvvigioni(false);
                      setProvvigioniData(null);
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
              
              {/* Body */}
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="sm:flex sm:items-start">
                  {/* Colonna sinistra: Immagine auto + Caratteristiche veicolo */}
                  <div className="sm:w-1/3 mb-4 sm:mb-0 sm:mr-4">
                  {/* Immagine auto */}
                    <div className="bg-blue-50 rounded-lg p-4 relative min-h-[150px] mb-4">
                    {getVehicleImage(selectedVehiclePublic) ? (
                      <img
                        src={getVehicleImage(selectedVehiclePublic)}
                        alt={`${selectedVehiclePublic.marca?.descrizione || selectedVehiclePublic.brand || ''} ${selectedVehiclePublic.modello || ''}`}
                        className="w-full h-full object-contain absolute inset-0 p-2"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/300x200?text=No+Image";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="h-32 w-32 bg-blue-200 rounded-full flex items-center justify-center">
                          <PhotoIcon className="h-16 w-16 text-blue-500" />
                        </div>
                      </div>
                    )}
                  </div>
                  
                    {/* Caratteristiche veicolo */}
                    <div className="border border-gray-200 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Caratteristiche veicolo</h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-gray-500">Marca</p>
                          <p className="font-medium">{selectedVehiclePublic.marca?.descrizione || selectedVehiclePublic.brand || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Modello</p>
                          <p className="font-medium">{selectedVehiclePublic.modello || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Allestimento</p>
                          <p className="font-medium">{selectedVehiclePublic.allestimento || '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Alimentazione</p>
                          <p className="font-medium">{selectedVehiclePublic.alimentazione || '-'}</p>
                        </div>
                        </div>
                      </div>
                    </div>
                    
                  {/* Colonna destra: Configura l'offerta + Dettagli */}
                  <div className="sm:w-2/3">
                    {/* Configura l'offerta */}
                    <div className="mb-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Configura l'offerta</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* Durata */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Durata</label>
                          <select
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={durataFilter}
                            onChange={(e) => {
                              setDurataFilter(e.target.value);
                              setCurrentFilterIndex(0);
                              setShowProvvigioni(false);
                              setProvvigioniData(null);
                            }}
                          >
                            {[...new Set(vehicleOffertePublic.map(o => o.durata))]
                              .sort((a, b) => a - b)
                              .map(durata => (
                                <option key={durata} value={durata.toString()}>
                                  {durata} mesi
                                </option>
                              ))}
                          </select>
                        </div>
                        
                        {/* Chilometri annui */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Chilometri annui</label>
                          <select
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={kmAnnuiFilter}
                            onChange={(e) => {
                              setKmAnnuiFilter(e.target.value);
                              setCurrentFilterIndex(0);
                              setShowProvvigioni(false);
                              setProvvigioniData(null);
                            }}
                          >
                            {[...new Set(vehicleOffertePublic.map(o => o.kmAnnui))]
                              .sort((a, b) => a - b)
                              .map(km => (
                                <option key={km} value={km.toString()}>
                                  {parseInt(km).toLocaleString()} km
                                </option>
                              ))}
                          </select>
                        </div>
                        
                        {/* Anticipo */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Anticipo</label>
                          <select
                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            value={anticipoFilterPublic}
                            onChange={(e) => {
                              setAnticipoFilterPublic(e.target.value);
                              setCurrentFilterIndex(0);
                              setShowProvvigioni(false);
                              setProvvigioniData(null);
                            }}
                          >
                            <option value="">Tutti gli anticipi</option>
                            {[...new Set(vehicleOffertePublic
                              .filter(offerta => 
                                (durataFilter ? offerta.durata.toString() === durataFilter : true) &&
                                (kmAnnuiFilter ? offerta.kmAnnui.toString() === kmAnnuiFilter : true)
                              )
                              .map(o => o.anticipo || 0))]
                              .sort((a, b) => a - b)
                              .map(anticipo => (
                                <option key={anticipo} value={anticipo.toString()}>
                                  {formatPrezzo(anticipo)}
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    
                    {/* Dettagli offerta selezionata */}
                    <div className="border-t border-gray-200 pt-4">
                      {getFilteredOffertePublic().length > 0 ? (
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">
                              Offerta {currentFilterIndex + 1} di {getFilteredOffertePublic().length}
                            </h4>
                            <div className="flex">
                              <button 
                                onClick={() => navigateOffertePublic('prev')}
                                className="mr-2 inline-flex items-center p-1 border border-gray-300 rounded-full shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                disabled={getFilteredOffertePublic().length <= 1}
                              >
                                <ChevronLeftIcon className="h-5 w-5" />
                              </button>
                              <button 
                                onClick={() => navigateOffertePublic('next')}
                                className="inline-flex items-center p-1 border border-gray-300 rounded-full shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                disabled={getFilteredOffertePublic().length <= 1}
                              >
                                <ChevronRightIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                          
                          {/* Dettagli offerta corrente */}
                          {getCurrentOffertaPublic() && (
                            <div className="bg-blue-50 rounded-lg p-4">
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                <div>
                                  <p className="text-sm text-gray-500">Canone Mensile</p>
                                  <p className="text-xl font-bold text-blue-600">
                                    {formatPrezzo(getCurrentOffertaPublic().canone)}
                                    <span className="text-sm font-normal ml-1">IVA escl.</span>
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Anticipo</p>
                                  <p className="font-medium">
                                    {getCurrentOffertaPublic().anticipo !== undefined && getCurrentOffertaPublic().anticipo !== null 
                                      ? formatPrezzo(getCurrentOffertaPublic().anticipo) 
                                      : formatPrezzo(0)}
                                    <span className="text-sm font-normal ml-1">IVA escl.</span>
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Società di Noleggio</p>
                                  <p className="font-medium">
                                    {getCurrentOffertaPublic().societaNoleggio?.nome || '-'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Stato Disponibilità</p>
                                  <p className="font-medium">
                                    {getCurrentOffertaPublic().statoDisponibilita || 'Ordine'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Penale Kasko</p>
                                  <p className="font-medium">
                                    {formatPenaleValue(getCurrentOffertaPublic().penaleKasko)}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-sm text-gray-500">Penale RCA</p>
                                  <p className="font-medium">
                                    {formatPenaleValue(getCurrentOffertaPublic().penaleRCA)}
                                  </p>
                              </div>
                                <div>
                                  <p className="text-sm text-gray-500">Penale Furto</p>
                                  <p className="font-medium">
                                    {formatPenaleValue(getCurrentOffertaPublic().penaleFurto)}
                                  </p>
                                </div>
                                {/* Campo provvigione nascosto, solo per elaborazioni interne */}
                                <input 
                                  type="hidden" 
                                  name="provvigione" 
                                  value={getCurrentOffertaPublic().provvigione || 0} 
                                />
                                
                                {/* Pulsante piccolo per mostrare provvigioni - poco visibile */}
                                <div className="col-span-full flex justify-end">
                                  <button
                                    onClick={toggleProvvigioni}
                                    className="text-xs text-gray-400 hover:text-gray-600 opacity-50 hover:opacity-100 transition-all duration-200"
                                    title="Visualizza calcolo provvigioni"
                                  >
                                    {showProvvigioni ? '👁️ Nascondi' : '💰'}
                                  </button>
                                </div>
                              </div>
                              
                              {/* Sezione provvigioni - mostrata solo se richiesta */}
                              {showProvvigioni && (
                                <div className="mt-4 border-t border-gray-200 pt-4">
                                  <div className="flex items-center justify-between mb-3">
                                    {loadingProvvigioni && (
                                      <div className="flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                                        <span className="text-xs text-gray-500">Calcolo...</span>
                            </div>
                          )}
                                  </div>
                                  
                                  {provvigioniData && !loadingProvvigioni && (
                                    <div>
                                      {provvigioniData.errore ? (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                          <p className="text-red-800 font-medium text-sm">Errore nel calcolo</p>
                                          <p className="text-red-600 text-xs mt-1">{provvigioniData.errore}</p>
                        </div>
                      ) : (
                                        <div>
                                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                                            <div className="flex justify-between items-center">
                                              <span className="text-sm font-medium text-green-800">Totale Provvigioni:</span>
                                              <span className="text-lg font-bold text-green-600">
                                                {formatCurrency(provvigioniData.provvigioneTotale)}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                        <p className="text-gray-500">Nessuna offerta disponibile con i filtri selezionati.</p>
                          <button 
                            onClick={() => {
                              setDurataFilter('');
                              setKmAnnuiFilter('');
                              setTipoClienteFilter('');
                              setAnticipoFilterPublic('');
                              setCurrentFilterIndex(0);
                            }}
                            className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Resetta i filtri
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="bg-gray-50 px-4 py-3 sm:px-6">
                {/* Messaggio di blocco per agenzie */}
                {isPreventiviBloccati() && (
                  <div className="mb-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex">
                        <InformationCircleIcon className="h-4 w-4 text-blue-400 mt-0.5" />
                        <div className="ml-2">
                          <p className="text-xs text-blue-800 font-medium">
                            Richiesta preventivi bloccata
                          </p>
                          <p className="text-xs text-blue-700 mt-1">
                            Non puoi richiedere preventivi nello stato attuale della pratica.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Quantità veicoli */}
                {!isPreventiviBloccati() && (
                  <div className="mb-3 flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-md px-4 py-2">
                    <label className="text-sm font-medium text-indigo-800" htmlFor="quantitaOffertaInput">
                      Quantità veicoli identici
                    </label>
                    <input
                      id="quantitaOffertaInput"
                      type="number"
                      min="1"
                      step="1"
                      value={quantitaOfferta}
                      onChange={(e) => setQuantitaOfferta(e.target.value)}
                      className="w-24 px-2 py-1 border border-indigo-300 rounded-md text-right focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      title="Numero di veicoli identici per questo preventivo"
                    />
                  </div>
                )}

                {/* Bottoni */}
                <div className="flex flex-col sm:flex-row-reverse gap-3">
                <button
                  type="button"
                    className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
                      isPreventiviBloccati() 
                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed' 
                        : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
                    }`}
                    disabled={isPreventiviBloccati()}
                  onClick={() => {
                      if (isPreventiviBloccati()) {
                        toast.warning('Non puoi richiedere preventivi nello stato attuale della pratica.');
                        return;
                      }
                      
                    const offertaSelezionata = getCurrentOffertaPublic();
                    if (offertaSelezionata) {
                      associaOffertaPratica(offertaSelezionata);
                    } else {
                      toast.warning('Seleziona prima un\'offerta valida.');
                    }
                  }}
                >
                    {isPreventiviBloccati() ? 'Preventivi bloccati' : 'Richiedi preventivo'}
                </button>
                  
                <button
                  type="button"
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => {
                      setShowModalPublic(false);
                      setSelectedVehiclePublic(null);
                      setVehicleOffertePublic([]);
                      setDurataFilter('');
                      setKmAnnuiFilter('');
                      setTipoClienteFilter('');
                      setAnticipoFilterPublic('');
                      setCurrentFilterIndex(0);
                      setShowProvvigioni(false);
                      setProvvigioniData(null);
                    }}
                >
                  Chiudi
                </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TabOfferte; 